<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const { accountId } = useAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.title);
const contacts = computed(() => {
  // Utiliser les données de l'API si disponibles, sinon les props statiques
  if (
    data.value?.mapped?.contacts &&
    Array.isArray(data.value.mapped.contacts)
  ) {
    return data.value.mapped.contacts;
  }
  if (data.value?.raw?.contacts && Array.isArray(data.value.raw.contacts)) {
    return data.value.raw.contacts;
  }
  if (data.value?.raw && Array.isArray(data.value.raw)) {
    return data.value.raw;
  }
  return props.widget.properties?.contacts || [];
});

const getInitials = name => {
  return name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatDate = date => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
};
</script>

<template>
  <div class="h-full flex flex-col font-inter group">
    <div v-if="title" class="mb-6 flex items-center justify-between px-1">
      <h3 class="text-[13px] font-bold text-slate-900 tracking-tight">
        {{ title }}
      </h3>
      <div
        class="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-50 border border-slate-100"
      >
        <span class="i-lucide-users h-3.5 w-3.5 text-slate-400" />
      </div>
    </div>
    <div class="flex-1 overflow-auto custom-scrollbar pr-1">
      <div
        v-if="loading && contacts.length === 0"
        class="flex items-center justify-center py-16"
      >
        <div class="flex flex-col items-center gap-4">
          <div
            class="h-10 w-10 border-2 border-slate-100 border-t-slate-950 rounded-full animate-spin"
          />
          <span
            class="text-[11px] font-bold text-slate-400 uppercase tracking-widest"
            >Loading...</span>
        </div>
      </div>
      <div v-else class="space-y-2 pb-4">
        <div
          v-for="(contact, index) in contacts"
          :key="contact.id || index"
          class="group/item flex items-center gap-4 p-4 transition-all duration-200 rounded-xl border border-slate-100 bg-white hover:border-slate-300 hover:bg-slate-50/50 cursor-pointer"
        >
          <div class="relative flex-shrink-0">
            <div
              class="h-11 w-11 rounded-xl border border-slate-100 overflow-hidden shadow-sm transition-all duration-500 group-hover/item:scale-105 group-hover/item:border-slate-300"
            >
              <img
                v-if="contact.avatar_url"
                :src="contact.avatar_url"
                :alt="contact.name"
                class="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-110"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-slate-50 text-slate-400 group-hover/item:text-slate-950 transition-colors"
              >
                <span class="text-xs font-bold uppercase tracking-tighter">{{
                  getInitials(contact.name)
                }}</span>
              </div>
            </div>
            <div
              class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full shadow-sm"
            />
          </div>

          <div class="flex-1 min-w-0">
            <p
              class="text-[13px] font-bold truncate text-slate-900 leading-tight"
            >
              {{ contact.name || 'Sans nom' }}
            </p>
            <div class="flex items-center gap-2 mt-0.5">
              <p
                v-if="contact.email"
                class="text-[11px] font-medium text-slate-400 truncate max-w-[180px]"
              >
                {{ contact.email }}
              </p>
            </div>
          </div>

          <div class="flex flex-col items-end gap-2 shrink-0">
            <span
              v-if="contact.last_activity_at || contact.updated_at"
              class="text-[9px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded-md"
            >
              {{ formatDate(contact.last_activity_at || contact.updated_at) }}
            </span>
            <div
              class="opacity-0 group-hover/item:opacity-100 -translate-x-2 group-hover/item:translate-x-0 transition-all duration-300"
            >
              <span class="i-lucide-arrow-right h-4 w-4 text-slate-950" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
